/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2024 (original work) Open Assessment Technologies SA ;
 */

import ApiError from 'core/error/ApiError';
import NetworkError from 'core/error/NetworkError';
import TimeoutError from 'core/error/TimeoutError';
import digest from 'core/digest';

/**
 * Contains all ongoing uploads, by interaction.
 * If a test taker changes, we cancel the current upload and start the new one.
 */
const uploads = new Map();

/**
 * Parse XML error sent by the Cloud Storage API
 * @param {string} xmlError - an xml string
 * @returns {Object?} with a code and a message
 */
function parseCloudStorageError(xmlError) {
    try {
        const parser = new DOMParser().parseFromString(xmlError, 'application/xml');
        if (parser && parser.documentElement) {
            const codeElement = parser.documentElement.querySelector('Code');
            const messageElement = parser.documentElement.querySelector('Message');

            return {
                code: codeElement && codeElement.textContent,
                message: messageElement && messageElement.textContent
            };
        }
    } catch (parsingErr) {
        return null;
    }
    return null;
}

/**
 * The upload service factory for Google Cloud Storage. The file is uploaded directly to a bucket from a signed URL.
 *
 * @param {string} interactionId - to identify an interaction
 * @param {Object} uploadServiceParameters - configuration for this upload from the server
 * @param {string} uploadServiceParameters.method - the HTTP method
 * @param {string} uploadServiceParameters.url - the signed URL for the upload
 * @param {Object} [uploadServiceParameters.linkParameters] - parameters to pass to the response, for linking
 * @param {Object} [config] - upload configuration from the interaction
 * @param {number} [config.timeout] - timeout for the upload requests
 * @returns {Object} the service
 */
export default function cloudStorageUploadServiceFactory(
    interactionId,
    { method, url, linkParameters } = {},
    { timeout } = {}
) {
    method = method || 'PUT';

    if (!url) {
        throw new TypeError('The signed URL is missing from uploadServiceParameters.');
    }

    //ensure we can access to window.subtle, Safari seems to not care...
    if ('isSecureContext' in window && window.isSecureContext === false) {
        throw new Error(`The page is not considered as secure, we won't be able to upload files.`);
    }

    return {
        name: 'cloud-storage',

        /**
         * Send the file to Cloud Storage
         * @param {Object} fileData
         * @param {File} fileData.data
         * @returns {Promise<Object>} resolves with the response value data
         */
        upload({ data: file }) {
            if (file instanceof File) {
                let timeoutId;
                let hasTimedout = false;

                const controller = new AbortController();
                const signal = controller.signal;

                const uploadPromise = Promise.all([this.cancel(), digest(file, 'SHA-256')])
                    .then(results => {
                        const hash = results[1];

                        uploads.set(interactionId, {
                            controller,
                            uploadPromise
                        });

                        const requestOptions = {
                            method,
                            body: file,
                            signal
                        };

                        if (timeout && timeout > 0) {
                            timeoutId = setTimeout(() => {
                                hasTimedout = true;
                                controller.abort('timeout');
                            }, timeout);
                        }

                        //TODO: reuse `packages/item-runner/src/runner/interactions/extendedText/cloudStorageRequest.js`
                        return fetch(url, requestOptions).then(response => {
                            uploads.delete(interactionId);
                            if (response.status === 200) {
                                return Object.assign(
                                    {
                                        data: hash,
                                        mime: file.type,
                                        name: file.name,
                                        localFile: file,
                                        version: response.headers.get('x-goog-generation')
                                    },
                                    linkParameters
                                );
                            }

                            return response.text().then(responseBody => {
                                if (response.status >= 400) {
                                    const parsedError = parseCloudStorageError(responseBody);
                                    if (parsedError) {
                                        return Promise.reject(
                                            new ApiError(
                                                `Cloud Storage ${parsedError.code}: ${parsedError.message}`,
                                                response.status,
                                                response
                                            )
                                        );
                                    }
                                }

                                return Promise.reject(
                                    new ApiError(
                                        'Unable to upload the file to Cloud Storage',
                                        response.status,
                                        response
                                    )
                                );
                            });
                        });
                    })
                    .catch(err => {
                        uploads.delete(interactionId);

                        if (err instanceof ApiError) {
                            return Promise.reject(err);
                        }

                        if (err instanceof DOMException) {
                            //timeout
                            if (hasTimedout || err.name === 'TimeoutError') {
                                return Promise.reject(new TimeoutError(err.message, timeout));
                            }
                            //request aborted internally
                            if (err.name === 'AbortError') {
                                return null;
                            }

                            //connectivity or cors
                            if (err.name === 'NetworkError') {
                                return Promise.reject(new NetworkError(err.message, 0));
                            }
                        }

                        //unknown err
                        return Promise.reject(err);
                    })
                    .finally(() => {
                        clearTimeout(timeoutId);
                    });
                return uploadPromise;
            }
            return Promise.resolve(null);
        },

        /**
         * Cancel the current upload
         * @param {string} [reason] - optional reason for the cancellation
         * @returns {Promise<boolean>} true if an ongoing upload has been canceled
         */
        cancel(reason) {
            if (uploads.has(interactionId)) {
                const previousUpload = uploads.get(interactionId);
                if (previousUpload.controller instanceof AbortController) {
                    previousUpload.controller.abort(reason);
                    return previousUpload.uploadPromise.then(() => true);
                }
            }
            return Promise.resolve(false);
        },

        /**
         * Get the response base type
         * @returns {string}  the base type
         */
        getBaseType() {
            return 'fileHash';
        }
    };
}
